import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";

import { RootState, AppDispatch } from "../../../../redux/store";
import { listenToShortGuestReservations } from "../../../../redux/shortstayReservationSlice";
import { listenToMultiShortstays } from "../../../../redux/shortstaysSlice";

import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

const PastShortAccomodations: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const reservations = useSelector(
    (state: RootState) => state.shortstayReservations.guestReservations
  );
  const listings = useSelector(
    (state: RootState) => state.shortstays.listings
  );

  // load reservations
  useEffect(() => {
    if (userId) {
      dispatch(listenToShortGuestReservations(userId));
    }
  }, [dispatch, userId]);

  // fetch missing listings
  useEffect(() => {
    const fetchedIds = listings.map((l) => l.listingId);
    const missing = reservations
      .map((r) => r.listingId)
      .filter((id) => !fetchedIds.includes(id));

    if (missing.length > 0) {
      const chunk = (arr: string[], size = 10) =>
        Array.from({ length: Math.ceil(arr.length / size) }, (_, i) =>
          arr.slice(i * size, i * size + size)
        );

      chunk(missing).forEach((ids) => {
        dispatch(listenToMultiShortstays(ids));
      });
    }
  }, [reservations, listings, dispatch]);

  const listingsMap = useMemo(() => {
    const map: Record<string, any> = {};
    listings.forEach((l) => (map[l.listingId] = l));
    return map;
  }, [listings]);

  const pastAndCancelled = useMemo(() => {
    return reservations
      .filter((r) => r.isCompleted || r.isCancelled)
      .sort(
        (a, b) =>
          dayjs(b.checkOut).valueOf() -
          dayjs(a.checkOut).valueOf()
      );
  }, [reservations]);

  if (pastAndCancelled.length === 0) {
    return <div style={{ opacity: 0.6 }}>No past stays yet.</div>;
  }

  return (
    <>
      {pastAndCancelled.map((res) => {
        const listing = listingsMap[res.listingId];
        if (!listing) return null;

        const needsReview =
          res.isCompleted && !res.hasReview;

        return (
          <section key={res.id} style={styles.listingCard}>
            <div style={styles.listingLeft}>
              <div style={styles.titleRow}>
                <strong style={styles.listingTitle}>
                  {listing.title}
                </strong>

                {res.isCancelled ? (
                  <span style={styles.badgeCancelled}>
                    <CancelIcon style={styles.badgeIcon} />
                    Cancelled
                  </span>
                ) : (
                  <span style={styles.badgePast}>
                    <CheckCircleIcon style={styles.badgeIcon} />
                    Past stay
                  </span>
                )}
              </div>

              <div style={styles.listingAddress}>
                {dayjs(res.checkIn).format("DD MMM")} –{" "}
                {dayjs(res.checkOut).format("DD MMM YYYY")}
              </div>

              {needsReview && (
                <button
                  style={styles.reviewBtn}
                  onClick={() =>
                    navigate(
                      `/shortrating/${userId}/${res.listingId}/${res.id}`
                    )
                  }
                >
                  Review stay
                </button>
              )}
            </div>

            <div style={styles.listingImageWrap}>
              <img
                src={listing.photos?.[0] || "/default-shortstay.jpg"}
                alt="listing"
                style={styles.listingImage}
              />
            </div>
          </section>
        );
      })}
    </>
  );
};

export default PastShortAccomodations;

/* ───────────── STYLES ───────────── */

const styles: Record<string, React.CSSProperties> = {
  listingCard: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 14,
    padding: 16,
    borderRadius: 16,
    border: "1px solid #eee",
    marginBottom: 12,
    background: "#fff",
  },

  listingLeft: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
    flex: 1,
  },

  titleRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
  },

  listingTitle: {
    fontSize: 15,
    fontWeight: 900,
    color: "#222",
    lineHeight: 1.25,
  },

  listingAddress: {
    fontSize: 13,
    color: "#717171",
  },

  listingImageWrap: {
    width: 64,
    height: 64,
    borderRadius: 12,
    overflow: "hidden",
    flexShrink: 0,
    boxShadow: "0 4px 10px rgba(0,0,0,0.12)",
  },

  listingImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },

  badgePast: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    background: "#111",
    color: "#fff",
    fontSize: 11,
    fontWeight: 800,
    padding: "4px 8px",
    borderRadius: 999,
  },

  badgeCancelled: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    background: "#cc0000",
    color: "#fff",
    fontSize: 11,
    fontWeight: 800,
    padding: "4px 8px",
    borderRadius: 999,
  },

  badgeIcon: {
    fontSize: 14,
  },

  reviewBtn: {
    marginTop: 6,
    alignSelf: "flex-start",
    background: "#111",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    padding: "6px 12px",
    fontWeight: 800,
    fontSize: 12,
    cursor: "pointer",
  },
};
